// Pure state machine. No DOM, no side effects.

export const Status = Object.freeze({
  IDLE:           'IDLE',
  SPEAKING:       'SPEAKING',
  PAUSED:         'PAUSED',
  COUNTING_DOWN:  'COUNTING_DOWN',
  WAITING_ANSWER: 'WAITING_ANSWER',
  RECORDING:      'RECORDING',
  TRANSCRIBING:   'TRANSCRIBING',
  CONFIRMING:     'CONFIRMING',    // reading transcript back, waiting to accept/re-record
  HISTORY_MODE:   'HISTORY_MODE',
  DONE:           'DONE',
})

function initial(report) {
  return {
    status:            Status.IDLE,
    report:            report ?? null,
    sections:          report?.sections ?? [],
    sectionIndex:      0,
    historyIndex:      null,   // null = live, N = viewing sections[N] in history
    timerRemaining:    0,
    timerTotal:        0,
    responses:         {},     // sectionId -> { transcript, selected }
    pausedFrom:        null,   // status we were in when PAUSED, for resume
    pendingTranscript: null,   // transcript waiting to be confirmed
    recordingFor:      null,   // section id we're recording a response for
    savedTimer:        0,      // timerRemaining before recording started (to restore)
    errorMessage:      null,
  }
}

function advance(state) {
  const next = state.sectionIndex + 1
  if (next >= state.sections.length) {
    return { ...state, status: Status.DONE, historyIndex: null, timerRemaining: 0 }
  }
  return { ...state, status: Status.SPEAKING, sectionIndex: next, historyIndex: null, timerRemaining: 0 }
}

// Commit pendingTranscript as the response for the current section,
// then return to COUNTING_DOWN (with savedTimer) or advance.
function acceptTranscript(state) {
  const section = state.sections[state.sectionIndex]
  if (!section) return advance(state)

  const selected = section.kind === 'multiple_choice'
    ? _parseOption(state.pendingTranscript ?? '', section.options ?? [])
    : null

  const responses = {
    ...state.responses,
    [section.id]: { transcript: state.pendingTranscript ?? '', selected },
  }

  const next = { ...state, responses, pendingTranscript: null, recordingFor: null, savedTimer: 0 }

  // Required question: advance immediately after accepting
  if (section.type === 'question' && section.required !== false) return advance(next)

  // Comment on a section: restore the countdown timer
  if (next.savedTimer > 0) {
    return { ...next, status: Status.COUNTING_DOWN, timerRemaining: next.savedTimer, timerTotal: next.savedTimer }
  }

  return advance(next)
}

// Try to parse "a", "option b", "the first one" etc. from a transcript.
function _parseOption(text, options) {
  const t = text.toLowerCase().trim()
  // exact option id or "option X" / "answer X"
  const m = t.match(/\b(?:option|answer|choice)?\s*([a-d])\b/)
  if (m) {
    const id = m[1]
    if (options.some(o => o.id === id)) return id
  }
  // match option text
  for (const opt of options) {
    if (t.includes(opt.text.toLowerCase().slice(0, 20))) return opt.id
  }
  return null
}

export function transition(state, action) {
  const { status, sectionIndex, historyIndex, sections } = state

  switch (action.type) {

    case 'BOOT': {
      if (status !== Status.IDLE) return state
      return { ...state, status: Status.SPEAKING }
    }

    case 'TTS_ENDED': {
      if (status !== Status.SPEAKING) return state
      const section = sections[sectionIndex]
      if (!section) return advance(state)
      if (section.type === 'question' && section.required !== false) {
        return { ...state, status: Status.WAITING_ANSWER }
      }
      const total = section.pause
      return { ...state, status: Status.COUNTING_DOWN, timerRemaining: total, timerTotal: total }
    }

    case 'TIMER_TICK': {
      if (status !== Status.COUNTING_DOWN && status !== Status.CONFIRMING) return state
      return { ...state, timerRemaining: Math.max(0, state.timerRemaining - 1) }
    }

    case 'TIMER_DONE': {
      if (status === Status.COUNTING_DOWN) return advance(state)
      if (status === Status.CONFIRMING)    return acceptTranscript(state)
      return state
    }

    // Single tap / headphone play-pause — context-sensitive:
    case 'PLAY_PAUSE': {
      const section = sections[sectionIndex]
      switch (status) {
        case Status.SPEAKING:
          return { ...state, status: Status.PAUSED, pausedFrom: Status.SPEAKING }

        case Status.PAUSED:
          return { ...state, status: state.pausedFrom ?? Status.SPEAKING, pausedFrom: null }

        case Status.COUNTING_DOWN:
          return { ...state, status: Status.RECORDING, recordingFor: section?.id ?? null, savedTimer: state.timerRemaining }

        case Status.WAITING_ANSWER:
          return { ...state, status: Status.RECORDING, recordingFor: section?.id ?? null, savedTimer: 0 }

        case Status.RECORDING:
          return { ...state, status: Status.TRANSCRIBING }

        case Status.CONFIRMING:
          return { ...state, status: Status.RECORDING, pendingTranscript: null }

        case Status.HISTORY_MODE:
          return { ...state, status: Status.SPEAKING, historyIndex: null }

        default: return state
      }
    }

    // Double tap while recording cancels it, restoring previous phase.
    case 'RECORD_CANCEL': {
      if (status !== Status.RECORDING) return state
      const section = sections[sectionIndex]
      const restore = section?.type === 'question'
        ? Status.WAITING_ANSWER
        : Status.COUNTING_DOWN
      return {
        ...state,
        status: restore,
        recordingFor: null,
        timerRemaining: state.savedTimer,
        timerTotal: state.timerTotal,
        savedTimer: 0,
        pendingTranscript: null,
      }
    }

    case 'TRANSCRIPT_READY': {
      if (status !== Status.TRANSCRIBING) return state
      const CONFIRM_SECS = 10
      return {
        ...state,
        status: Status.CONFIRMING,
        pendingTranscript: action.transcript,
        timerRemaining: CONFIRM_SECS,
        timerTotal: CONFIRM_SECS,
      }
    }

    case 'TRANSCRIPT_ERROR': {
      if (status !== Status.TRANSCRIBING) return state
      const section = sections[sectionIndex]
      const restore = section?.type === 'question'
        ? Status.WAITING_ANSWER
        : Status.COUNTING_DOWN
      return {
        ...state,
        status: restore,
        recordingFor: null,
        timerRemaining: state.savedTimer,
        timerTotal: state.timerTotal,
        savedTimer: 0,
        errorMessage: action.message,
      }
    }

    case 'NEXT': {
      if (status === Status.IDLE || status === Status.DONE) return state
      if (status === Status.HISTORY_MODE) {
        if (historyIndex === null) return state
        const next = historyIndex + 1
        return next >= sectionIndex
          ? { ...state, status: Status.SPEAKING, historyIndex: null }
          : { ...state, historyIndex: next }
      }
      return advance(state)
    }

    case 'PREV': {
      if (status === Status.IDLE || status === Status.DONE) return state
      if (status === Status.HISTORY_MODE) {
        if (historyIndex === null || historyIndex <= 0) return state
        return { ...state, historyIndex: historyIndex - 1 }
      }
      if (sectionIndex <= 0) return state
      return { ...state, status: Status.HISTORY_MODE, historyIndex: sectionIndex - 1 }
    }

    case 'ANSWER': {
      if (status !== Status.WAITING_ANSWER) return state
      const section = sections[sectionIndex]
      const responses = {
        ...state.responses,
        [section.id]: { transcript: action.transcript ?? '', selected: action.selected ?? null },
      }
      return advance({ ...state, responses })
    }

    case 'LOAD_REPORT': {
      if (status !== Status.IDLE) return state
      return { ...state, report: action.report, sections: action.report?.sections ?? [] }
    }

    case 'SET_ERROR':
      return { ...state, errorMessage: action.message }

    case 'CLEAR_ERROR':
      return { ...state, errorMessage: null }

    default:
      return state
  }
}

export function createStore(report) {
  let state = initial(report)
  const listeners = new Set()

  function dispatch(action) {
    const prev = state
    state = transition(state, action)
    if (state !== prev) {
      listeners.forEach(fn => fn(state, prev))
    }
  }

  return {
    getState: () => state,
    dispatch,
    subscribe(fn) {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
  }
}
